import React, { useState, useEffect, useMemo, useRef, useCallback, useDeferredValue, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  CircularProgress,
  Backdrop,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  WarningAmber as WarningAmberIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { getPaymentSettlements, processSettlement, markPaymentAsSettled } from '../api/adminPanelApi';

// Format currency for display
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount);
};

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getCheckoutDate = (s) => {
  const d =
    s?.settlement_date ||
    s?.details?.checkout_date ||
    s?.details?.check_out_date ||
    s?.check_out_date;
  return d ? new Date(d).getTime() : 0;
};

const isSettled = (s) => {
  const status = (s?.settlement_status || s?.details?.status || '').toLowerCase();
  return status === 'transferred' || !!s?.payment_settled;
};

const isAccountMissing = (s) =>
  s?.account_details_missing === true ||
  s?.details?.account_details_missing === true;

/** Cutoff timestamps: settlement older than N calendar months. */
const monthsAgoTs = (months) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() - months);
  return d.getTime();
};

/** Yellow = older than 1 month; red = older than 6 months. */
const getAgeRowSx = (s) => {
  const ts = getCheckoutDate(s);
  if (!ts) return {};
  if (ts < monthsAgoTs(6)) {
    return {
      backgroundColor: '#ffcdd2',
      '&:hover': { backgroundColor: '#ef9a9a', cursor: 'pointer' },
    };
  }
  if (ts < monthsAgoTs(1)) {
    return {
      backgroundColor: '#fff9c4',
      '&:hover': { backgroundColor: '#fff59d', cursor: 'pointer' },
    };
  }
  return {
    '&:hover': { backgroundColor: 'rgba(255, 119, 0, 0.04)', cursor: 'pointer' },
  };
};

const emptySummary = () => ({
  count: 0,
  gross_amount: 0,
  commission_amount: 0,
  hotel_amount: 0,
  pending_transfer: { count: 0, gross_amount: 0, commission_amount: 0, hotel_amount: 0 },
  missing_account_pending: { count: 0, gross_amount: 0, commission_amount: 0, hotel_amount: 0 },
});

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

const buildApiFilters = ({
  fromDate,
  toDate,
  month,
  excludeCancelled,
  excludeTestHotels = true,
  includePast6Months = false,
}) => {
  const filters = {
    include_past_6_months: includePast6Months ? 1 : 0,
    // ON (default): omit/1 — API excludes test hotels. OFF: 0 to include them.
    exclude_test_hotels: excludeTestHotels ? 1 : 0,
  };
  if (fromDate && toDate) {
    filters.from_date = fromDate;
    filters.to_date = toDate;
  } else if (month) {
    filters.month = month;
  }
  if (excludeCancelled) {
    filters.exclude_cancelled = 1;
  }
  return filters;
};

/** Keep overall card totals; drop heavy row arrays from the list response. */
const slimOverall = (overall) => {
  if (!overall) return { missing_account_pending: null, past_6_months: null };
  const slimBlock = (block) => {
    if (!block) return null;
    const { data, ...rest } = block;
    return rest;
  };
  return {
    missing_account_pending: slimBlock(overall.missing_account_pending),
    past_6_months: slimBlock(overall.past_6_months),
  };
};

const todayStart = () => new Date(new Date().toISOString().slice(0, 10)).getTime();

const displayOrNa = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

/** Map API account_info (bank_details + route_account) into flat display fields. */
const normalizeAccountInfo = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const bank = raw.bank_details || {};
  const route = raw.route_account || {};
  const hasContent =
    raw.hotel_id != null ||
    raw.hotel_name ||
    Object.values(bank).some((v) => v != null && v !== '') ||
    route.account_id != null ||
    raw.razorpay_account_id ||
    raw.account_number ||
    raw.contact_name ||
    raw.email;
  if (!hasContent) return null;

  return {
    hotel_id: raw.hotel_id,
    hotel_name: raw.hotel_name,
    account_holder_name: bank.account_holder_name || raw.contact_name,
    account_number: bank.account_number || raw.account_number,
    bank_name: bank.bank_name,
    ifsc_code: bank.ifsc_code,
    gst_number: bank.gst_number,
    pan_number: bank.pan_number,
    postal_code: bank.postal_code,
    razorpay_account_id: route.account_id ?? raw.razorpay_account_id ?? null,
    route_created: route.route_created ?? raw.route_created,
    isActive: route.isActive ?? raw.isActive,
    account_details_changed: raw.account_details_changed,
    changed_fields: Array.isArray(raw.changed_fields) ? raw.changed_fields : [],
    email: raw.email,
  };
};

function AccountDetailRow({ label, value, booleanField = false }) {
  let display = displayOrNa(value);
  if (booleanField) {
    if (value === true || value === 1 || value === '1') display = 'Yes';
    else if (value === false || value === 0 || value === '0') display = 'No';
    else display = 'N/A';
  }
  return (
    <Box>
      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
        {label}:
      </Typography>{' '}
      <Typography variant="body2" component="span" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
        {display}
      </Typography>
    </Box>
  );
}

/** Coalesce parallel getPaymentSettlements calls with the same filters. */
let paymentSettlementsInFlight = null;
let paymentSettlementsInFlightKey = null;
const requestPaymentSettlements = (filters = {}, force = false) => {
  const key = JSON.stringify(filters || {});
  if (!force && paymentSettlementsInFlight && paymentSettlementsInFlightKey === key) {
    return paymentSettlementsInFlight;
  }
  paymentSettlementsInFlightKey = key;
  paymentSettlementsInFlight = getPaymentSettlements(filters).finally(() => {
    paymentSettlementsInFlight = null;
    paymentSettlementsInFlightKey = null;
  });
  return paymentSettlementsInFlight;
};

const PaymentSettlements = () => {
  const navigate = useNavigate();
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [overall, setOverall] = useState({ missing_account_pending: null, past_6_months: null });
  const [overallDetailRows, setOverallDetailRows] = useState({ missing_account: null, past_6m: null });
  const [overallDetailLoading, setOverallDetailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearch = useDeferredValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | settled
  const [sortCheckout, setSortCheckout] = useState('desc'); // desc = latest first within group
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [markSettledDialogOpen, setMarkSettledDialogOpen] = useState(false);
  const [transferId, setTransferId] = useState('');
  const [markingSettled, setMarkingSettled] = useState(false);
  const [upcomingDialogOpen, setUpcomingDialogOpen] = useState(false);
  const [overallDialog, setOverallDialog] = useState({ open: false, type: null }); // missing_account | past_6m
  // Draft filter controls — empty date/month = all history (API default)
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [month, setMonth] = useState('');
  const [excludeCancelled, setExcludeCancelled] = useState(false);
  const [excludeTestHotels, setExcludeTestHotels] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(() =>
    buildApiFilters({
      fromDate: '',
      toDate: '',
      month: '',
      excludeCancelled: false,
      excludeTestHotels: true,
    })
  );
  const appliedFiltersRef = useRef(appliedFilters);
  const initialFetchStarted = useRef(false);
  const overallDetailCacheKey = useRef('');

  const fetchPaymentSettlements = useCallback(async ({ force = false, notify = true, filters } = {}) => {
    const nextFilters = {
      ...(filters !== undefined ? filters : appliedFiltersRef.current),
      include_past_6_months: 0, // always off for list loads
    };
    setLoading(true);
    try {
      const result = await requestPaymentSettlements(nextFilters, force);
      if (result.success) {
        const payload = result.data || {};
        const settlementsData = payload.data || [];
        setSettlements(settlementsData);
        setPage(0);
        setExpandedRows(new Set());
        setSummary({
          ...emptySummary(),
          ...(payload.summary || {}),
          pending_transfer: {
            ...emptySummary().pending_transfer,
            ...(payload.summary?.pending_transfer || {}),
          },
          missing_account_pending: {
            ...emptySummary().missing_account_pending,
            ...(payload.summary?.missing_account_pending || {}),
          },
        });
        setOverall(slimOverall(payload.overall));
        // Invalidate overall drill-down cache when list filters change
        setOverallDetailRows({ missing_account: null, past_6m: null });
        overallDetailCacheKey.current = '';
        if (notify) {
          setSnackbar({
            open: true,
            message: settlementsData.length === 0
              ? 'No payment settlements found'
              : `Loaded ${settlementsData.length} payment settlement${settlementsData.length !== 1 ? 's' : ''} successfully`,
            severity: settlementsData.length === 0 ? 'info' : 'success',
          });
        }
      } else {
        if (notify) {
          setSnackbar({
            open: true,
            message: result.error || 'Failed to fetch payment settlements',
            severity: 'error',
          });
        }
        if (result.error?.toLowerCase().includes('token') || result.error?.toLowerCase().includes('auth')) {
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      }
    } catch (error) {
      if (notify) {
        setSnackbar({
          open: true,
          message: 'Failed to fetch payment settlements',
          severity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (initialFetchStarted.current) return;
    initialFetchStarted.current = true;
    fetchPaymentSettlements({ notify: true, filters: appliedFiltersRef.current });
  }, [fetchPaymentSettlements]);

  const handleApplyFilters = () => {
    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      setSnackbar({
        open: true,
        message: 'Please set both From and To dates for a date range',
        severity: 'warning',
      });
      return;
    }
    const next = buildApiFilters({ fromDate, toDate, month, excludeCancelled, excludeTestHotels });
    appliedFiltersRef.current = next;
    setAppliedFilters(next);
    fetchPaymentSettlements({ force: true, notify: true, filters: next });
  };

  const handleResetFilters = () => {
    setFromDate('');
    setToDate('');
    setMonth('');
    setExcludeCancelled(false);
    setExcludeTestHotels(true);
    const next = buildApiFilters({
      fromDate: '',
      toDate: '',
      month: '',
      excludeCancelled: false,
      excludeTestHotels: true,
    });
    appliedFiltersRef.current = next;
    setAppliedFilters(next);
    fetchPaymentSettlements({ force: true, notify: true, filters: next });
  };

  const handleMonthChange = (value) => {
    setMonth(value);
    if (value) {
      setFromDate('');
      setToDate('');
    }
  };

  const handleDateChange = (field, value) => {
    if (field === 'from') setFromDate(value);
    else setToDate(value);
    if (value) setMonth('');
  };

  const openOverallDialog = async (type) => {
    setOverallDialog({ open: true, type });
    const cacheKey = `${type}|c${appliedFiltersRef.current.exclude_cancelled ? 1 : 0}|t${appliedFiltersRef.current.exclude_test_hotels === 0 ? 0 : 1}`;
    const cached =
      type === 'missing_account' ? overallDetailRows.missing_account : overallDetailRows.past_6m;
    if (cached && overallDetailCacheKey.current === cacheKey) return;

    setOverallDetailLoading(true);
    try {
      // Overall ignores date/month — cancelled + test-hotel flags still apply
      const result = await getPaymentSettlements({
        exclude_cancelled: appliedFiltersRef.current.exclude_cancelled ? 1 : undefined,
        exclude_test_hotels: appliedFiltersRef.current.exclude_test_hotels === 0 ? 0 : 1,
        include_past_6_months: 1,
      });
      if (result.success) {
        const o = result.data?.overall || {};
        const missingRows = o.missing_account_pending?.data || [];
        const pastRows = o.past_6_months?.data || [];
        setOverallDetailRows({ missing_account: missingRows, past_6m: pastRows });
        overallDetailCacheKey.current = cacheKey;
        // Refresh card totals from this response too
        setOverall(slimOverall(o));
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to load overall details',
          severity: 'error',
        });
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: 'Failed to load overall details',
        severity: 'error',
      });
    } finally {
      setOverallDetailLoading(false);
    }
  };

  // Main list: past/today only; search + status; unsettled first then settlement date
  const filteredSettlements = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    const today = todayStart();
    const mult = sortCheckout === 'asc' ? 1 : -1;
    const scored = [];

    for (let i = 0; i < settlements.length; i++) {
      const s = settlements[i];
      const ts = getCheckoutDate(s);
      if (ts > today) continue;

      if (query) {
        const bid = s.booking_id != null ? String(s.booking_id) : '';
        const hid = s.hotel_id != null ? String(s.hotel_id) : '';
        const hname = (s.hotel_name || s.details?.hotel_name || '').toLowerCase();
        const gname = (s.guest_name || s.details?.guest_name || '').toLowerCase();
        if (
          !bid.includes(query) &&
          !hid.includes(query) &&
          !hname.includes(query) &&
          !gname.includes(query)
        ) {
          continue;
        }
      }

      const settled = isSettled(s);
      if (statusFilter === 'pending' && settled) continue;
      if (statusFilter === 'settled' && !settled) continue;

      scored.push({ s, ts, settled });
    }

    scored.sort((a, b) => {
      if (a.settled !== b.settled) return a.settled ? 1 : -1;
      return (a.ts - b.ts) * mult;
    });
    return scored.map((x) => x.s);
  }, [settlements, deferredSearch, statusFilter, sortCheckout]);

  const pagedSettlements = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSettlements.slice(start, start + rowsPerPage);
  }, [filteredSettlements, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [deferredSearch, statusFilter, sortCheckout]);

  const selectedAccountInfo = useMemo(
    () => normalizeAccountInfo(selectedSettlement?.details?.account_info),
    [selectedSettlement]
  );

  // Only scan for upcoming when the dialog is open
  const upcomingSettlements = useMemo(() => {
    if (!upcomingDialogOpen) return [];
    const query = deferredSearch.trim().toLowerCase();
    const today = todayStart();
    const out = [];
    for (let i = 0; i < settlements.length; i++) {
      const s = settlements[i];
      if (getCheckoutDate(s) <= today || isSettled(s)) continue;
      if (query) {
        const bid = s.booking_id != null ? String(s.booking_id) : '';
        const hid = s.hotel_id != null ? String(s.hotel_id) : '';
        const hname = (s.hotel_name || s.details?.hotel_name || '').toLowerCase();
        const gname = (s.guest_name || s.details?.guest_name || '').toLowerCase();
        if (
          !bid.includes(query) &&
          !hid.includes(query) &&
          !hname.includes(query) &&
          !gname.includes(query)
        ) {
          continue;
        }
      }
      out.push(s);
    }
    return out;
  }, [upcomingDialogOpen, settlements, deferredSearch]);

  // Toggle row expansion
  const toggleRowExpansion = (bookingId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedRows(newExpanded);
  };

  // Handle process settlement
  const handleProcessSettlement = async () => {
    if (!selectedSettlement) return;

    setProcessing(true);
    try {
      const result = await processSettlement(selectedSettlement.booking_id);
      
      if (result.success) {
        const transferId = result.data.razorpay_response?.id;
        const transferAmount = result.data.transfer_amount_rupees;
        const hotelName = result.data.hotel_name;
        
        setSnackbar({
          open: true,
          message: `Settlement processed successfully! Transfer ID: ${transferId || 'N/A'}, Amount: ${formatCurrency(transferAmount)}`,
          severity: 'success'
        });
        
        setProcessDialogOpen(false);
        setSelectedSettlement(null);
        
        // Refresh the settlements list to get updated status
        await fetchPaymentSettlements({ force: true, notify: false });
      } else {
        // Handle specific error messages from API
        let errorMessage = result.error || 'Failed to process settlement';
        
        // Check for specific error details in the response
        if (result.data) {
          // If there's a validation error with amounts
          if (result.data.booking_amount_paise && result.data.transfer_amount_paise) {
            errorMessage = `${errorMessage} (Booking: ${formatCurrency(result.data.booking_amount_paise / 100)}, Transfer: ${formatCurrency(result.data.transfer_amount_paise / 100)})`;
          }
          
          // If there's a Razorpay error response
          if (result.data.razorpay_response?.error) {
            const razorpayError = result.data.razorpay_response.error;
            errorMessage = `${errorMessage} - ${razorpayError.description || razorpayError.code || 'Razorpay API Error'}`;
          }
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while processing settlement',
        severity: 'error'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Handle mark payment as settled
  const handleMarkAsSettled = async () => {
    if (!selectedSettlement || !transferId.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a transfer ID',
        severity: 'warning'
      });
      return;
    }

    setMarkingSettled(true);
    try {
      const result = await markPaymentAsSettled(selectedSettlement.booking_id, transferId.trim());
      
      if (result.success) {
        setSnackbar({
          open: true,
          message: `Payment marked as settled successfully for booking #${selectedSettlement.booking_id}`,
          severity: 'success'
        });
        
        setMarkSettledDialogOpen(false);
        setSelectedSettlement(null);
        setTransferId('');
        
        // Refresh the settlements list to get updated status
        await fetchPaymentSettlements({ force: true, notify: false });
      } else {
        setSnackbar({
          open: true,
          message: result.error || 'Failed to mark payment as settled',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while marking payment as settled',
        severity: 'error'
      });
    } finally {
      setMarkingSettled(false);
    }
  };

  // Format value for display (handles boolean conversion)
  const formatValue = (key, value) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    // Check if the key suggests it's a boolean field
    const keyLower = key.toLowerCase();
    const isBooleanField = /active|isactive|is_active|enabled|disabled|status/i.test(keyLower);
    
    // If value is 0 or 1, and it's a boolean field, convert to Yes/No
    if (isBooleanField && (value === 0 || value === 1 || value === '0' || value === '1')) {
      return value === 1 || value === '1' ? 'Yes' : 'No';
    }
    
    // Check if value is boolean
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return String(value);
  };

  // Format status chip
  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase() || '';
    const statusConfig = {
      captured: { color: '#4caf50', label: 'Captured' },
      transferred: { color: '#2196f3', label: 'Transferred' },
      failed: { color: '#f44336', label: 'Failed' },
      refunded: { color: '#ff9800', label: 'Refunded' }
    };
    
    const config = statusConfig[statusLower] || { color: '#757575', label: status || 'Unknown' };
    
    return (
      <Chip 
        label={config.label}
        size="small"
        sx={{ 
          backgroundColor: config.color,
          color: 'white',
          fontWeight: 500
        }}
      />
    );
  };

  // Booking status chip
  const getBookingStatusChip = (status) => {
    const statusLower = status?.toLowerCase() || '';
    const configMap = {
      confirmed: { color: '#4caf50', label: 'Confirmed' },
      cancelled: { color: '#f44336', label: 'Cancelled' },
      'checked-in': { color: '#1976d2', label: 'Checked-in' },
      'checked-out': { color: '#9c27b0', label: 'Checked-out' },
      noshow: { color: '#ff9800', label: 'No Show' },
      'no show': { color: '#ff9800', label: 'No Show' }
    };
    const config = configMap[statusLower] || { color: '#757575', label: status || 'Unknown' };
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: 'white',
          fontWeight: 500
        }}
      />
    );
  };

  const overallMissing = overall.missing_account_pending;
  const overallPast6m = overall.past_6_months;
  const overallDialogRows =
    overallDialog.type === 'missing_account'
      ? (overallDetailRows.missing_account || [])
      : overallDialog.type === 'past_6m'
        ? (overallDetailRows.past_6m || [])
        : [];
  const hasDateOrMonthFilter = Boolean(
    appliedFilters.from_date || appliedFilters.to_date || appliedFilters.month
  );
  const upcomingCount = useMemo(() => {
    const today = todayStart();
    let n = 0;
    for (let i = 0; i < settlements.length; i++) {
      const s = settlements[i];
      if (getCheckoutDate(s) > today && !isSettled(s)) n += 1;
    }
    return n;
  }, [settlements]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Loading Backdrop */}
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              onClick={() => navigate('/')} 
              sx={{ 
                color: '#FF7700',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 119, 0, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
              Payment Settlements
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchPaymentSettlements({ force: true })}
            disabled={loading}
            sx={{ 
              borderColor: '#FF7700',
              color: '#FF7700',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 119, 0, 0.04)'
              }
            }}
          >
            Refresh
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View and manage payment settlements with booking and financial details
        </Typography>
      </Box>

      {/* API date / cancelled filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 2 }}>
          Date &amp; cancelled filters
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From date"
              type="date"
              value={fromDate}
              onChange={(e) => handleDateChange('from', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={Boolean(month)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To date"
              type="date"
              value={toDate}
              onChange={(e) => handleDateChange('to', e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={Boolean(month)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Month"
              type="month"
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={Boolean(fromDate || toDate)}
              helperText={fromDate && toDate ? 'Date range wins' : ' '}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={excludeCancelled}
                  onChange={(e) => setExcludeCancelled(e.target.checked)}
                  color="warning"
                />
              }
              label="Hide cancelled"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={excludeTestHotels}
                  onChange={(e) => setExcludeTestHotels(e.target.checked)}
                  color="warning"
                />
              }
              label="Exclude test hotels"
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={loading}
              sx={{ backgroundColor: '#FF7700', '&:hover': { backgroundColor: '#e65100' } }}
            >
              Apply
            </Button>
            <Button variant="outlined" onClick={handleResetFilters} disabled={loading}>
              Reset
            </Button>
            {hasDateOrMonthFilter && (
              <Chip
                label={appliedFilters.month ? `Month ${appliedFilters.month}` : 'Date range'}
                size="small"
                color="warning"
                sx={{ alignSelf: 'center' }}
              />
            )}
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          No date/month filter loads all history. Past-6m row details load when you open that card.
        </Typography>
      </Paper>

      {/* Filtered summary cards */}
      <Typography variant="subtitle2" sx={{ color: '#2E4057', mb: 1, fontWeight: 600 }}>
        Filtered summary
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', border: '1px solid #90caf9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ReceiptIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#1565c0' }}>
                    {summary.count ?? settlements.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1976d2' }}>Count</Typography>
                  <Typography variant="caption" sx={{ color: '#1565c0', display: 'block' }}>
                    Hotel {formatCurrency(summary.hotel_amount)} · Comm {formatCurrency(summary.commission_amount)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', border: '1px solid #ffcc80' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AttachMoneyIcon sx={{ fontSize: 24, color: '#f57c00' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#ef6c00', fontSize: { xs: '1.1rem', md: '1.35rem' } }}>
                    {formatCurrency(summary.gross_amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f57c00' }}>Gross amount</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)', border: '1px solid #ffe082' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SendIcon sx={{ fontSize: 24, color: '#f9a825' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#f57f17', fontSize: { xs: '1.1rem', md: '1.35rem' } }}>
                    {formatCurrency(summary.pending_transfer?.hotel_amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f9a825' }}>
                    Pending transfer ({summary.pending_transfer?.count ?? 0})
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)', border: '1px solid #f48fb1' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <WarningAmberIcon sx={{ fontSize: 24, color: '#c2185b' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#ad1457', fontSize: { xs: '1.1rem', md: '1.35rem' } }}>
                    {formatCurrency(summary.missing_account_pending?.hotel_amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#c2185b' }}>
                    Missing account (filtered) ({summary.missing_account_pending?.count ?? 0})
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Overall cards (ignore date/month filters) */}
      <Typography variant="subtitle2" sx={{ color: '#2E4057', mb: 1, fontWeight: 600 }}>
        Overall (ignores date/month
        {appliedFilters.exclude_cancelled ? '; cancelled hidden' : ''}
        {appliedFilters.exclude_test_hotels === 0 ? '; test hotels included' : '; test hotels excluded'})
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card
            onClick={() => openOverallDialog('missing_account')}
            sx={{
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #fbe9e7 0%, #ffccbc 100%)',
              border: '1px solid #ffab91',
              '&:hover': { boxShadow: 4 },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningAmberIcon sx={{ fontSize: 28, color: '#d84315' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#bf360c' }}>
                    Needs transfer — account N/A
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#e64a19', my: 0.5 }}>
                    {formatCurrency(overallMissing?.hotel_amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#d84315' }}>
                    {overallMissing?.count ?? 0} pending with bank details missing — click for list
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            onClick={() => openOverallDialog('past_6m')}
            sx={{
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #eceff1 0%, #cfd8dc 100%)',
              border: '1px solid #b0bec5',
              '&:hover': { boxShadow: 4 },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <HistoryIcon sx={{ fontSize: 28, color: '#455a64' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#37474f' }}>
                    Older than 6 months
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: '#455a64', my: 0.5 }}>
                    {formatCurrency(overallPast6m?.hotel_amount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#546e7a' }}>
                    Pending {formatCurrency(overallPast6m?.pending_hotel_amount)} · {overallPast6m?.count ?? 0} rows
                    {overallPast6m?.cutoff_date ? ` · before ${overallPast6m.cutoff_date}` : ''} — click for list
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Client filter / sort bar */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057', mb: 2 }}>
          Search &amp; sort
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by Booking ID, Hotel ID, Hotel Name, or Guest Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="settled">Settled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort by settlement date</InputLabel>
              <Select value={sortCheckout} label="Sort by settlement date" onChange={(e) => setSortCheckout(e.target.value)}>
                <MenuItem value="desc">Latest first</MenuItem>
                <MenuItem value="asc">Oldest first</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setUpcomingDialogOpen(true)}
              sx={{ borderColor: '#FF7700', color: '#FF7700' }}
            >
              Upcoming settlements ({upcomingCount})
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Ordered by settlement date; unsettled first, then settled at bottom.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: '#2E4057', fontWeight: 600 }}>Age:</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: '#fff9c4', border: '1px solid #fbc02d' }} />
            <Typography variant="caption" color="text.secondary">&gt; 1 month</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: '#ffcdd2', border: '1px solid #e57373' }} />
            <Typography variant="caption" color="text.secondary">&gt; 6 months</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Settlements Table */}
      {filteredSettlements.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchQuery 
              ? 'No settlements found matching your search criteria.' 
              : settlements.length === 0 
                ? 'No payment settlements found' 
                : 'No settlements match the search criteria.'}
          </Typography>
        </Paper>
      ) : (
        <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057', width: 56 }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Booking ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel ID</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Hotel Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Guest Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Booking Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Check-in</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Check-out</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057' }}>Settlement</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2E4057', textAlign: 'center' }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedSettlements.map((settlement, rowIndex) => {
                const details = settlement.details || {};
                const isExpanded = expandedRows.has(settlement.booking_id);
                const accountInfo = isExpanded
                  ? normalizeAccountInfo(details.account_info)
                  : null;
                const bookingStatus = settlement.booking_status || details.booking_status;
                const isBookingConfirmed = (bookingStatus || '').toLowerCase() === 'confirmed';
                const serialNo = page * rowsPerPage + rowIndex + 1;
                const ageRowSx = getAgeRowSx(settlement);
                return (
                  <React.Fragment key={settlement.booking_id}>
                    <TableRow 
                      hover
                      sx={ageRowSx}
                      onClick={() => toggleRowExpansion(settlement.booking_id)}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {serialNo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#2E4057' }}>
                          {settlement.booking_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#2E4057' }}>
                          {settlement.hotel_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#2E4057' }}>
                          {settlement.hotel_name || details.hotel_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#2E4057' }}>
                          {settlement.guest_name || details.guest_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getBookingStatusChip(bookingStatus)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ color: '#2E4057' }}>
                            {formatDate(details.check_in_date || settlement.check_in_date)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ color: '#2E4057' }}>
                            {formatDate(details.check_out_date || settlement.check_out_date)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                          <Chip
                            label={isSettled(settlement) ? 'Settled' : (settlement.settlement_status || 'Pending')}
                            size="small"
                            sx={{
                              backgroundColor: isSettled(settlement) ? '#4caf50' : '#ff9800',
                              color: 'white',
                              fontWeight: 500,
                              textTransform: 'capitalize',
                            }}
                          />
                          {(settlement.settlement_date || details.checkout_date) && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(settlement.settlement_date || details.checkout_date)}
                            </Typography>
                          )}
                          {isAccountMissing(settlement) && (
                            <Chip
                              label="Account N/A"
                              size="small"
                              icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
                              sx={{
                                backgroundColor: '#fce4ec',
                                color: '#c2185b',
                                fontWeight: 600,
                                height: 22,
                                '& .MuiChip-icon': { color: '#c2185b' },
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton 
                          size="small"
                          sx={{ 
                            color: '#FF7700',
                            '&:hover': { 
                              backgroundColor: 'rgba(255, 119, 0, 0.1)'
                            }
                          }}
                        >
                          {expandedRows.has(settlement.booking_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expandedRows.has(settlement.booking_id)} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#2E4057', fontWeight: 600 }}>
                              Settlement Details
                            </Typography>
                            
                            <Grid container spacing={3}>
                              {/* Hotel Information */}
                              <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 3, backgroundColor: 'white' }}>
                                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <BusinessIcon sx={{ fontSize: 20 }} /> Hotel Information
                                  </Typography>
                                  <Divider sx={{ mb: 2 }} />
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Hotel Name:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {details.hotel_name || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Guest Name:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {details.guest_name || settlement.guest_name || 'N/A'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Check-in Date:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {formatDate(details.check_in_date)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Check-out Date:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {formatDate(details.check_out_date)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Checkout Time:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                        {details.checkout_time || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>
                              </Grid>

                              {/* Financial Details */}
                              <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 3, backgroundColor: 'white' }}>
                                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AttachMoneyIcon sx={{ fontSize: 20 }} /> Financial Details
                                  </Typography>
                                  <Divider sx={{ mb: 2 }} />
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Gross Amount:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        {formatCurrency(details.gross_amount)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Commission Amount:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: '#f44336', fontWeight: 600 }}>
                                        {formatCurrency(details.commission_amount)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Hotel Amount:
                                      </Typography>{' '}
                                      <Typography variant="body2" component="span" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                        {formatCurrency(details.hotel_amount)}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#2E4057' }}>
                                        Status:
                                      </Typography>{' '}
                                      {getStatusChip(details.status)}
                                    </Box>
                                    {details.status?.toLowerCase() === 'captured' && isBookingConfirmed && (
                                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {accountInfo && (
                                          <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<SendIcon />}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedSettlement(settlement);
                                              setProcessDialogOpen(true);
                                            }}
                                            disabled={processing}
                                            sx={{
                                              backgroundColor: '#4caf50',
                                              '&:hover': { backgroundColor: '#388e3c' }
                                            }}
                                          >
                                            Process Settlement
                                          </Button>
                                        )}
                                        <Button
                                          variant="outlined"
                                          fullWidth
                                          startIcon={<ReceiptIcon />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedSettlement(settlement);
                                            setTransferId('');
                                            setMarkSettledDialogOpen(true);
                                          }}
                                          disabled={markingSettled}
                                          sx={{
                                            borderColor: '#FF7700',
                                            color: '#FF7700',
                                            '&:hover': {
                                              borderColor: '#e65100',
                                              backgroundColor: 'rgba(255, 119, 0, 0.04)'
                                            }
                                          }}
                                        >
                                          Mark as Settled
                                        </Button>
                                      </Box>
                                    )}
                                  </Box>
                                </Paper>
                              </Grid>

                              {/* Account Information */}
                              {accountInfo ? (
                                <Grid item xs={12} md={4}>
                                  <Paper sx={{ p: 3, backgroundColor: 'white' }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057', display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <AccountBalanceIcon sx={{ fontSize: 20 }} /> Hotel Account Information
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5c6bc0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Bank details
                                      </Typography>
                                      <AccountDetailRow label="Account holder" value={accountInfo.account_holder_name} />
                                      <AccountDetailRow label="Account number" value={accountInfo.account_number} />
                                      <AccountDetailRow label="Bank name" value={accountInfo.bank_name} />
                                      <AccountDetailRow label="IFSC code" value={accountInfo.ifsc_code} />
                                      <AccountDetailRow label="GST number" value={accountInfo.gst_number} />
                                      <AccountDetailRow label="PAN number" value={accountInfo.pan_number} />
                                      <AccountDetailRow label="Postal code" value={accountInfo.postal_code} />
                                      {accountInfo.email && (
                                        <AccountDetailRow label="Email" value={accountInfo.email} />
                                      )}
                                      <Divider sx={{ my: 0.5 }} />
                                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5c6bc0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Razorpay route
                                      </Typography>
                                      <AccountDetailRow
                                        label="Route account ID"
                                        value={accountInfo.razorpay_account_id != null ? accountInfo.razorpay_account_id : 'Not created'}
                                      />
                                      <AccountDetailRow label="Route created" value={accountInfo.route_created} booleanField />
                                      <AccountDetailRow label="Is active" value={accountInfo.isActive} booleanField />
                                      {accountInfo.account_details_changed && accountInfo.changed_fields.length > 0 && (
                                        <Box>
                                          <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                                            Changed fields:
                                          </Typography>{' '}
                                          <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                                            {accountInfo.changed_fields.join(', ')}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Paper>
                                </Grid>
                              ) : (
                                <Grid item xs={12} md={4}>
                                  <Paper sx={{ p: 3, backgroundColor: 'white', border: '1px dashed #e0e0e0' }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#2E4057', display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <AccountBalanceIcon sx={{ fontSize: 20, color: '#9e9e9e' }} /> Hotel Account Information
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      py: 3,
                                      textAlign: 'center'
                                    }}>
                                      <AccountBalanceIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                                      <Typography variant="body1" sx={{ color: '#757575', fontWeight: 500, mb: 0.5 }}>
                                        No Account Details
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                                        There are no account details for this hotel
                                      </Typography>
                                    </Box>
                                  </Paper>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredSettlements.length}
          page={page}
          onPageChange={(_e, nextPage) => {
            startTransition(() => {
              setPage(nextPage);
              setExpandedRows(new Set());
            });
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            startTransition(() => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
              setExpandedRows(new Set());
            });
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        />
        </Paper>
      )}

      {/* Upcoming Settlements Dialog */}
      <Dialog
        open={upcomingDialogOpen}
        onClose={() => setUpcomingDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon sx={{ color: '#FF7700' }} /> Upcoming settlements
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bookings whose checkout date is in the future and not yet settled.
          </Typography>
          {upcomingSettlements.length === 0 ? (
            <Typography color="text.secondary">No upcoming settlements.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, width: 56 }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Booking ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Hotel</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Guest</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Checkout</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Hotel amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {upcomingSettlements.map((s, idx) => {
                    const details = s.details || {};
                    const checkout = details.check_out_date || s.check_out_date;
                    return (
                      <TableRow key={s.booking_id}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{s.booking_id}</TableCell>
                        <TableCell>{s.hotel_name || details.hotel_name || 'N/A'}</TableCell>
                        <TableCell>{s.guest_name || details.guest_name || 'N/A'}</TableCell>
                        <TableCell>{formatDate(checkout)}</TableCell>
                        <TableCell align="right">{formatCurrency(details.hotel_amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpcomingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Process Settlement Confirmation Dialog */}
      <Dialog
        open={processDialogOpen}
        onClose={() => !processing && setProcessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#fff3e0',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <SendIcon sx={{ color: '#FF7700', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Process Settlement
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4, px: 3 }}>
          <DialogContentText sx={{ mb: 3, color: '#2E4057', fontSize: '1rem' }}>
            Are you sure you want to process settlement for booking <strong>#{selectedSettlement?.booking_id}</strong>?
          </DialogContentText>
          
          {selectedSettlement && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 2,
              mb: 2
            }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#2E4057' }}>
                Settlement Details:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Booking ID:</strong> {selectedSettlement.booking_id}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Hotel:</strong> {selectedSettlement.hotel_name || selectedSettlement.details?.hotel_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Guest:</strong> {selectedSettlement.guest_name || selectedSettlement.details?.guest_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Amount to Transfer:</strong> {formatCurrency(selectedSettlement.details?.hotel_amount)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Account holder:</strong> {selectedAccountInfo?.account_holder_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Account number:</strong> {selectedAccountInfo?.account_number || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>IFSC:</strong> {selectedAccountInfo?.ifsc_code || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Route account ID:</strong>{' '}
                  {selectedAccountInfo?.razorpay_account_id != null ? selectedAccountInfo.razorpay_account_id : 'Not created'}
                </Typography>
              </Box>
            </Box>
          )}
          
          <DialogContentText sx={{ color: '#757575', fontSize: '0.875rem', fontStyle: 'italic' }}>
            This action will transfer the hotel amount to the linked Razorpay account. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setProcessDialogOpen(false)}
            variant="outlined"
            disabled={processing}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessSettlement}
            variant="contained"
            disabled={processing}
            startIcon={processing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SendIcon />}
            sx={{ 
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#388e3c' },
              borderRadius: 2,
              px: 3
            }}
          >
            {processing ? 'Processing...' : 'Process Settlement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark as Settled Dialog */}
      <Dialog
        open={markSettledDialogOpen}
        onClose={() => !markingSettled && setMarkSettledDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          py: 3,
          backgroundColor: '#fff3e0',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#FF7700', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} sx={{ color: '#2E4057' }}>
              Mark Payment as Settled
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ py: 4, px: 3 }}>
          <DialogContentText sx={{ mb: 3, color: '#2E4057', fontSize: '1rem' }}>
            Enter the transfer ID for booking <strong>#{selectedSettlement?.booking_id}</strong>
          </DialogContentText>
          
          {selectedSettlement && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 2,
              mb: 3
            }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#2E4057' }}>
                Booking Details:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Booking ID:</strong> {selectedSettlement.booking_id}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Hotel:</strong> {selectedSettlement.hotel_name || selectedSettlement.details?.hotel_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Guest:</strong> {selectedSettlement.guest_name || selectedSettlement.details?.guest_name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Amount:</strong> {formatCurrency(selectedSettlement.details?.hotel_amount)}
                </Typography>
              </Box>
            </Box>
          )}
          
          <TextField
            autoFocus
            margin="dense"
            label="Transfer ID"
            type="text"
            fullWidth
            variant="outlined"
            value={transferId}
            onChange={(e) => setTransferId(e.target.value)}
            placeholder="Enter transfer ID (e.g., trf_ABC123XYZ)"
            disabled={markingSettled}
            sx={{ mb: 2 }}
          />
          
          <DialogContentText sx={{ color: '#757575', fontSize: '0.875rem', fontStyle: 'italic' }}>
            Please enter the transfer ID to mark this payment as settled.
          </DialogContentText>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => {
              setMarkSettledDialogOpen(false);
              setTransferId('');
              setSelectedSettlement(null);
            }}
            variant="outlined"
            disabled={markingSettled}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsSettled}
            variant="contained"
            disabled={markingSettled || !transferId.trim()}
            startIcon={markingSettled ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ReceiptIcon />}
            sx={{ 
              backgroundColor: '#FF7700',
              '&:hover': { backgroundColor: '#e65100' },
              borderRadius: 2,
              px: 3
            }}
          >
            {markingSettled ? 'Marking...' : 'Mark as Settled'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Overall drill-down dialog */}
      <Dialog
        open={overallDialog.open}
        onClose={() => setOverallDialog({ open: false, type: null })}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#2E4057' }}>
            {overallDialog.type === 'missing_account'
              ? 'Needs transfer — account N/A'
              : 'Settlements older than 6 months'}
          </Typography>
          {overallDialog.type === 'past_6m' && overallPast6m?.cutoff_date && (
            <Typography variant="body2" color="text.secondary">
              Settlement date before {overallPast6m.cutoff_date} · Pending hotel amount{' '}
              {formatCurrency(overallPast6m.pending_hotel_amount)}
            </Typography>
          )}
          {overallDialog.type === 'missing_account' && (
            <Typography variant="body2" color="text.secondary">
              {overallMissing?.count ?? 0} rows · Hotel amount {formatCurrency(overallMissing?.hotel_amount)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {overallDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : overallDialogRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No rows in this overall section.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 56 }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Booking ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Hotel</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Guest</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Settlement date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Hotel amount</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overallDialogRows.map((row, idx) => (
                    <TableRow key={row.booking_id} hover sx={getAgeRowSx(row)}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.booking_id}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.hotel_name || row.details?.hotel_name || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">#{row.hotel_id}</Typography>
                      </TableCell>
                      <TableCell>{row.guest_name || row.details?.guest_name || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.settlement_status || row.details?.status || (isSettled(row) ? 'transferred' : 'pending')}
                          size="small"
                          sx={{
                            textTransform: 'capitalize',
                            backgroundColor: isSettled(row) ? '#4caf50' : '#ff9800',
                            color: 'white',
                          }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(row.settlement_date || row.details?.checkout_date)}</TableCell>
                      <TableCell>{formatCurrency(row.details?.hotel_amount ?? row.hotel_amount)}</TableCell>
                      <TableCell>
                        {isAccountMissing(row) ? (
                          <Chip label="N/A" size="small" color="error" variant="outlined" />
                        ) : (
                          'OK'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOverallDialog({ open: false, type: null })} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PaymentSettlements;

